import React, { useEffect, useState } from 'react';
import { ArrowLeft, Check, ChevronRight, ExternalLink, HeartHandshake, RefreshCw } from 'lucide-react';
import { AREAS, CATEGORIES } from '@/data';
import { fetchCoupleActivity } from '@/lib/admin-api';
import type { AdminActivityEvent, AdminActivityKind, AdminCoupleActivity, AdminCoupleSummary, Clip } from '@/types';
import { activityState, deviceLabel, lastActiveAt, shortDate, timeAgo } from './people-format';
import { CopyLinkButton, coupleLoginLink } from '@/components/CopyLinkButton';

interface CoupleRecordProps {
  couple: AdminCoupleSummary;
  clips: Clip[];
  loginTrackingEnabled: boolean;
  onBack: () => void;
  onOpenCard: (coupleId: string) => void;
  onOpenReviewer: (reviewerId: string) => void;
  onOpenSubmissions: () => void;
}

type TimelineFilter = 'all' | 'logins' | 'clips' | 'messages';

const FILTER_KINDS: Record<Exclude<TimelineFilter, 'all'>, AdminActivityKind[]> = {
  logins: ['login'],
  clips: ['tried', 'scheduled', 'completed', 'submission', 'wishlist'],
  messages: ['message', 'coach-message', 'answered', 'request'],
};

const DOT_COLOURS: Record<AdminActivityKind, string> = {
  login: 'bg-him',
  message: 'bg-her',
  'coach-message': 'bg-her/50',
  tried: 'bg-action',
  completed: 'bg-action',
  scheduled: 'bg-action/50',
  submission: 'bg-attention',
  wishlist: 'bg-attention/50',
  answered: 'bg-ink-2',
  request: 'bg-ink-2',
};

const card = 'bg-zinc-900/60 border border-zinc-700 rounded-2xl';

// "2026-10-02" → "Fri 2 Oct" (plain date strings, so no timezone shift)
const formatDateKey = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) return key;
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

const StatTile: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <div className={`${card} p-4 min-w-0`}>
    <p className="text-xs font-semibold text-zinc-400">{label}</p>
    <p className="text-2xl font-bold text-white mt-1 truncate">{value}</p>
    {hint && <p className="text-xs text-zinc-500 mt-0.5 truncate">{hint}</p>}
  </div>
);

export const CoupleRecord: React.FC<CoupleRecordProps> = ({ couple, clips, loginTrackingEnabled, onBack, onOpenCard, onOpenReviewer, onOpenSubmissions }) => {
  const [activity, setActivity] = useState<AdminCoupleActivity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<TimelineFilter>('all');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchCoupleActivity(couple.coupleId)
      .then(result => { if (!cancelled) setActivity(result); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [couple.coupleId, reloadKey]);

  // Never show another couple's timeline while the new one loads
  const current = activity?.coupleId === couple.coupleId ? activity : null;
  const name = [couple.hisName, couple.herName].filter(Boolean).join(' & ') || couple.coupleId;
  const state = activityState(couple);
  const events = (current?.events ?? []).filter(e => filter === 'all' || FILTER_KINDS[filter].includes(e.kind));

  const clipTitle = (clipId?: string, known?: string) => known ?? clips.find(c => c.id === clipId)?.title ?? 'a clip';
  // Function replacer so titles containing "$&"-style patterns render literally
  const eventTitle = (e: AdminActivityEvent) => e.title.replace('{clip}', () => clipTitle(e.clipId, e.clipTitle));
  const nextDate = current?.nextDate;

  const moods = new Map<string, number>();
  for (const { clipId, categoryId, count } of current?.triedClips ?? []) {
    const area = CATEGORIES.find(c => c.id === (clips.find(cl => cl.id === clipId)?.categoryId ?? categoryId))?.area;
    const label = area && area in AREAS ? AREAS[area as keyof typeof AREAS].name : 'Other';
    moods.set(label, (moods.get(label) ?? 0) + count);
  }
  const moodList = [...moods.entries()].sort((a, b) => b[1] - a[1]);

  const needs = [
    couple.pendingSubmissionCount > 0 && {
      key: 'submissions',
      title: `${couple.pendingSubmissionCount} clip submission${couple.pendingSubmissionCount === 1 ? '' : 's'} to review`,
      hint: couple.lastSubmissionAt ? `Latest sent ${timeAgo(couple.lastSubmissionAt)}` : undefined,
      action: 'Review',
      onClick: onOpenSubmissions,
    },
    couple.unreadFromCoupleCount > 0 && {
      key: 'messages',
      title: `${couple.unreadFromCoupleCount} unread message${couple.unreadFromCoupleCount === 1 ? '' : 's'}`,
      hint: couple.lastMessageAt ? `Last one ${timeAgo(couple.lastMessageAt)}` : undefined,
      action: 'Reply',
      onClick: () => onOpenCard(couple.coupleId),
    },
    couple.pendingRequestCount > 0 && {
      key: 'requests',
      title: `${couple.pendingRequestCount} content request${couple.pendingRequestCount === 1 ? '' : 's'}`,
      hint: undefined,
      action: 'Open',
      onClick: () => onOpenCard(couple.coupleId),
    },
  ].filter((n): n is Exclude<typeof n, false> => !!n);

  const profileChips: { label: string; done: boolean }[] = [
    { label: 'His profile', done: couple.hasHisProfile },
    { label: 'Her profile', done: couple.hasHerProfile },
    { label: 'Her survey', done: !!couple.surveyCompletedAt },
    { label: 'Feedback', done: !!couple.feedbackSubmittedAt },
  ];

  const loginPlace = [deviceLabel(couple.lastLoginUserAgent), couple.lastLoginCity || couple.lastLoginCountry].filter(Boolean).join(' · ');

  return (
    <div className="space-y-5">
      <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
        <button onClick={onBack} className="flex items-center gap-1.5 text-zinc-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> People
        </button>
        <span className="text-zinc-600">/</span>
        <span className="text-white font-medium truncate">{name}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <span className="flex shrink-0" aria-hidden="true">
            <span className="w-14 h-14 rounded-full bg-him-tint text-him text-lg font-bold flex items-center justify-center border-[3px] border-zinc-800">
              {(couple.hisName || '?').charAt(0).toUpperCase()}
            </span>
            <span className="w-14 h-14 -ml-4 rounded-full bg-her-tint text-her text-lg font-bold flex items-center justify-center border-[3px] border-zinc-800">
              {(couple.herName || '?').charAt(0).toUpperCase()}
            </span>
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-3xl font-medium text-white truncate">{name}</h2>
            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
              <span className="px-2 py-0.5 rounded-md bg-zinc-700 text-zinc-300 font-mono">{couple.coupleId}</span>
              <CopyLinkButton url={coupleLoginLink(couple.coupleId)} />
              <span className="text-zinc-400">Joined {shortDate(couple.setupAt)}</span>
              {state === 'online' ? (
                <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> Online now{couple.currentPage ? ` · ${couple.currentPage}` : ''}
                </span>
              ) : (
                <span className="text-zinc-400">Last active {timeAgo(lastActiveAt(couple))}</span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => onOpenCard(couple.coupleId)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-zinc-600 text-sm font-semibold text-white hover:bg-zinc-700"
        >
          Open couple card <ExternalLink className="w-4 h-4" />
        </button>
      </div>

      {/* Relationships */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div className={`${card} p-4`}>
          <p className="text-xs font-semibold text-zinc-400 mb-2">Coach</p>
          {couple.coaches.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {couple.coaches.map(coach => (
                <button
                  key={coach.id}
                  onClick={() => onOpenReviewer(coach.id)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-action-tint text-action text-sm font-semibold hover:brightness-110"
                >
                  <HeartHandshake className="w-4 h-4" /> {coach.label}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No coach assigned</p>
          )}
        </div>
        <div className={`${card} p-4`}>
          <p className="text-xs font-semibold text-zinc-400 mb-2">Reviewer’s own couple?</p>
          {couple.ownerReviewer ? (
            <button onClick={() => onOpenReviewer(couple.ownerReviewer!.id)} className="text-sm font-semibold text-him hover:underline">
              Yes · {couple.ownerReviewer.label}
            </button>
          ) : (
            <p className="text-sm text-white">No</p>
          )}
        </div>
        <div className={`${card} p-4`}>
          <p className="text-xs font-semibold text-zinc-400 mb-2">Account run by</p>
          <p className="text-sm text-white capitalize">{couple.managedBy || 'Not set'}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile
          label="Last login"
          value={loginTrackingEnabled ? timeAgo(couple.lastLoginAt) : '—'}
          hint={loginTrackingEnabled ? loginPlace || undefined : 'Tracking not set up'}
        />
        <StatTile label="Logins" value={couple.loginCount} hint={`${couple.loginsLast30d} in the last 30 days`} />
        <StatTile label="Clips tried" value={couple.triedCount} hint={couple.lastTriedAt ? `Last one ${timeAgo(couple.lastTriedAt)}` : 'None yet'} />
        <StatTile
          label="Submissions"
          value={couple.submissionCount}
          hint={couple.pendingSubmissionCount > 0 ? `${couple.pendingSubmissionCount} awaiting review` : `${couple.approvedSubmissionCount} approved`}
        />
        <StatTile
          label="Upcoming dates"
          value={couple.upcomingCount}
          hint={nextDate ? `${formatDateKey(nextDate.scheduledDate)} · ${clipTitle(nextDate.clipId, nextDate.clipTitle)}` : `${couple.completedCount} done`}
        />
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-5 items-start">
        {/* Timeline */}
        <section className={`${card} p-5`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-bold text-white">Activity</h3>
            <div className="flex items-center gap-1">
              {(['all', 'logins', 'clips', 'messages'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  aria-pressed={filter === f}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize ${filter === f ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`}
                >
                  {f}
                </button>
              ))}
              <button onClick={() => setReloadKey(k => k + 1)} aria-label="Refresh activity" className="ml-1 p-1.5 rounded-lg text-zinc-400 hover:text-white">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
          {error ? (
            <p className="text-sm text-red-400">Couldn’t load activity: {error}</p>
          ) : !current ? (
            <p className="text-sm text-zinc-500">Loading activity…</p>
          ) : events.length === 0 ? (
            <p className="text-sm text-zinc-500">Nothing here yet.</p>
          ) : (
            <ol className="relative">
              {events.map((e, i) => (
                <li key={`${e.at}-${e.kind}-${i}`} className="grid grid-cols-[72px_16px_minmax(0,1fr)] gap-3 pb-4 last:pb-0">
                  <span className="text-xs text-zinc-500 pt-0.5 text-right" title={new Date(e.at).toLocaleString('en-GB')}>{timeAgo(e.at)}</span>
                  <span className="relative flex justify-center">
                    <span className={`mt-1.5 w-2.5 h-2.5 rounded-full ${DOT_COLOURS[e.kind]}`} />
                    {i < events.length - 1 && <span className="absolute top-5 -bottom-4 w-px bg-zinc-700" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm text-white">{eventTitle(e)}</span>
                    {e.detail && (
                      <span className="block text-xs text-zinc-400 mt-0.5 break-words">
                        {e.kind === 'scheduled' ? `for ${formatDateKey(e.detail)}` : e.detail}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <div className="space-y-5">
          {/* Needs you */}
          <section className={`${card} p-5`}>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-lg font-bold text-white">Needs you</h3>
              {needs.length > 0 && <span className="px-2 py-0.5 rounded-full bg-attention-tint text-attention text-xs font-bold">{needs.length}</span>}
            </div>
            {needs.length === 0 ? (
              <p className="text-sm text-zinc-500 flex items-center gap-1.5"><Check className="w-4 h-4 text-action" /> Nothing waiting on you</p>
            ) : (
              <div className="space-y-2">
                {needs.map(n => (
                  <div key={n.key} className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800">
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-white">{n.title}</span>
                      {n.hint && <span className="block text-xs text-zinc-400">{n.hint}</span>}
                    </span>
                    <button onClick={n.onClick} className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-action text-ground text-xs font-bold">
                      {n.action} <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {couple.awaitingCoupleCount > 0 && (
              <p className="text-xs text-zinc-400 mt-3">
                {couple.awaitingCoupleCount} question{couple.awaitingCoupleCount === 1 ? '' : 's'} waiting on them to answer
              </p>
            )}
          </section>

          {/* Profile */}
          <section className={`${card} p-5`}>
            <h3 className="text-lg font-bold text-white mb-3">Profile</h3>
            <div className="flex flex-wrap gap-2">
              {profileChips.map(chip => (
                <span
                  key={chip.label}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${chip.done ? 'bg-action-tint text-action' : 'bg-zinc-800 text-zinc-500'}`}
                >
                  {chip.done && <Check className="w-3.5 h-3.5" />}
                  {chip.done ? chip.label : `${chip.label} · not yet`}
                </span>
              ))}
            </div>
            <p className="text-xs text-zinc-400 mt-3">
              {couple.preferencesCount} category preference{couple.preferencesCount === 1 ? '' : 's'} · {couple.favoritesCount} favourite{couple.favoritesCount === 1 ? '' : 's'} · {couple.wishlistCount} on wishlist
            </p>
          </section>

          {/* Moods */}
          <section className={`${card} p-5`}>
            <h3 className="text-lg font-bold text-white mb-3">Moods they go for</h3>
            {moodList.length === 0 ? (
              <p className="text-sm text-zinc-500">{current ? 'Nothing tried yet' : 'Loading…'}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {moodList.map(([label, count]) => (
                  <span key={label} className="px-2.5 py-1 rounded-full bg-her-tint text-her text-xs font-semibold">
                    {label} · {count}
                  </span>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};
